/**
 * Supabase (PostgreSQL) implementation of DataRepository.
 * Reads go through the `v_tender_summary` view; writes go to base tables.
 * Business rules are enforced in the database (constraints, triggers, RLS) —
 * this adapter only translates errors into DomainError for the UI.
 */
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type {
  AlertDelivery,
  MonthlyEstimate,
  ApprovingAuthority,
  BudgetHead,
  GlAccount,
  AlertDeliveryStatus,
  TenderAlertEvent,
  TextChannelStatus,
  Amendment,
  AppNotification,
  AppSettings,
  AppUser,
  AuditLog,
  Department,
  FinancialTransaction,
  Location,
  QuantityTransaction,
  TenderLineItem,
  TenderDocument,
  TenderSummary,
  Vendor,
  VendorSummary,
} from '@/types/domain';
import {
  DomainError,
  type DataRepository,
  type Paged,
  type PendingDocumentRow,
  type TenderQuery,
  type TenderSortField,
  type QuantityLedgerRow,
  type FinancialLedgerRow,
  type PendingAmendmentRow,
  type LookupKind,
} from '../repository';
import { camelize, snakify, toSnake } from '@/lib/case';
import { env } from '@/config/env';
import { computeDashboard } from '../shared/aggregations';

type Row = Record<string, unknown>;

/**
 * Rows from queries whose select string is built at runtime (e.g. `*, ${this.tenderJoin}`).
 * supabase-js cannot infer a row type from a non-literal string, so we treat them as plain rows.
 */
const asRows = (data: unknown): Row[] => (data ?? []) as Row[];

function fail(error: PostgrestError | null): void {
  if (!error) return;
  if (error.code === '23505' && /gem_id/i.test(error.message + (error.details ?? ''))) {
    throw new DomainError('DUPLICATE_GEM_ID', 'A tender with this GEM ID already exists.');
  }
  if (error.code === '23505' && /crfq|tender_number/i.test(error.message + (error.details ?? ''))) {
    throw new DomainError('DUPLICATE_CRFQ', 'A tender with this CRFQ Number already exists.');
  }
  if (error.code === '23505' && /_code_key|\bcode\b/i.test(error.message + (error.details ?? ''))) {
    throw new DomainError('VALIDATION', 'This code is already used. Codes must be unique.');
  }
  if (error.code === '23505') throw new DomainError('VALIDATION', 'A record with the same unique value already exists.');
  if (error.code === '23503') throw new DomainError('IN_USE', 'This record is still used by other records and cannot be deleted. Deactivate it instead.');
  if (error.code === '23514') throw new DomainError('VALIDATION', `Validation failed: ${error.message}`);
  if (error.code === '42501') throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
  // Business-rule guards in the database (e.g. consumption edit/delete checks) raise 22023 with a readable message.
  if (error.code === '22023' || error.code === 'P0001') throw new DomainError('VALIDATION', error.message);
  throw new DomainError('UNKNOWN', error.message);
}

const rangeOf = (page = 1, pageSize = 25): [number, number] => [(page - 1) * pageSize, page * pageSize - 1];

/** Escape user input for PostgREST `or=(…ilike…)` filters. */
const like = (term: string) => `%${term.replace(/[%_,()]/g, ' ').trim()}%`;

const SORT_COLUMN: Record<TenderSortField, string> = {
  gemId: 'gem_id', title: 'title', vendorName: 'vendor_name', departmentName: 'department_name',
  contractValue: 'contract_value', startDate: 'start_date', endDate: 'end_date',
  utilizationPct: 'utilization_pct', status: 'status', daysRemaining: 'days_remaining', updatedAt: 'updated_at',
  billProcessedTill: 'bill_processed_till',
};

/** Tender column referencing each master-data list. */
const LOOKUP_FK: Record<LookupKind, string> = {
  departments: 'department_id', locations: 'location_id', approving_authorities: 'approving_authority_id',
  gl_accounts: 'gl_account_id', budget_heads: 'budget_head_id',
};

const mapEstimate = (r: Row): MonthlyEstimate => ({
  ...camelize<MonthlyEstimate>(r),
  estimatedAmount: Number(r.estimated_amount),
  estimatedQuantity: r.estimated_quantity == null ? null : Number(r.estimated_quantity),
});

const mapSummary = (r: Row) => {
  const s = camelize<TenderSummary>(r);
  // numeric columns arrive as strings from PostgREST
  for (const k of ['contractValue', 'totalQuantity', 'consumedQuantity', 'remainingQuantity', 'utilizationPct',
    'invoicedAmount', 'paidAmount', 'penaltyAmount', 'pendingDocuments', 'documentsToReview', 'amendmentsPending', 'lineItemCount'] as const) {
    (s as unknown as Record<string, number>)[k] = Number(s[k] ?? 0);
  }
  s.repeatCount = Number(s.repeatCount ?? 0);
  s.orderType = s.orderType ?? 'original';
  s.budgetStatus = s.budgetStatus ?? 'none';
  s.budgetAmountSought = s.budgetAmountSought == null ? null : Number(s.budgetAmountSought);
  return s;
};

export class SupabaseRepository implements DataRepository {
  readonly mode = 'supabase' as const;
  constructor(private sb: SupabaseClient) {}

  private async uid(): Promise<string | null> {
    const { data } = await this.sb.auth.getUser();
    return data.user?.id ?? null;
  }

  // ------------------------------------------------------------ tenders
  tenders: DataRepository['tenders'] = {
    list: async (q: TenderQuery): Promise<Paged<TenderSummary>> => {
      const page = q.page ?? 1;
      const pageSize = q.pageSize ?? 25;
      let query = this.sb.from('v_tender_summary').select('*', { count: 'exact' });
      const term = q.search?.trim();
      if (term) {
        const v = like(term);
        const cols: Record<string, string[]> = {
          all: ['gem_id', 'tender_number', 'vendor_name', 'vendor_code', 'po_number', 'contract_number', 'title'],
          gemId: ['gem_id'], tenderNumber: ['tender_number'], vendor: ['vendor_name', 'vendor_code'],
          poNumber: ['po_number'], contractNumber: ['contract_number'],
        };
        query = query.or(cols[q.searchField ?? 'all'].map((c) => `${c}.ilike.${v}`).join(','));
      }
      if (q.statuses?.length) query = query.in('status', q.statuses);
      if (q.departmentId) query = query.eq('department_id', q.departmentId);
      if (q.locationId) query = query.eq('location_id', q.locationId);
      if (q.vendorId) query = query.eq('vendor_id', q.vendorId);
      if (q.startFrom) query = query.gte('start_date', q.startFrom);
      if (q.startTo) query = query.lte('start_date', q.startTo);
      if (q.endFrom) query = query.gte('end_date', q.endFrom);
      if (q.endTo) query = query.lte('end_date', q.endTo);
      if (q.valueMin != null) query = query.gte('contract_value', q.valueMin);
      if (q.valueMax != null) query = query.lte('contract_value', q.valueMax);
      if (q.utilizationMin != null) query = query.gte('utilization_pct', q.utilizationMin);
      if (q.utilizationMax != null) query = query.lte('utilization_pct', q.utilizationMax);
      if (q.tenderingMethod) query = query.eq('tendering_method', q.tenderingMethod);
      if (q.tenderCategory) query = query.eq('tender_category', q.tenderCategory);
      if (q.floatedBy) query = query.eq('floated_by', q.floatedBy);
      if (q.detailsMissing) query = query.eq('details_missing', true);
      if (q.orderType) query = query.eq('order_type', q.orderType);
      if (q.repeatOfTenderId) query = query.eq('repeat_of_tender_id', q.repeatOfTenderId);
      if (q.budget === 'open') query = query.in('budget_status', ['needed', 'requested']);
      else if (q.budget) query = query.eq('budget_status', q.budget);
      if (q.approvingAuthorityId === 'none') query = query.is('approving_authority_id', null);
      else if (q.approvingAuthorityId) query = query.eq('approving_authority_id', q.approvingAuthorityId);
      if (q.budgetHeadId === 'none') query = query.is('budget_head_id', null);
      else if (q.budgetHeadId) query = query.eq('budget_head_id', q.budgetHeadId);
      if (q.glGroup === 'none') query = query.is('gl_account_group', null);
      else if (q.glGroup) query = query.eq('gl_account_group', q.glGroup);
      const sort = q.sort ?? { field: 'updatedAt', direction: 'desc' };
      query = query.order(SORT_COLUMN[sort.field], { ascending: sort.direction === 'asc', nullsFirst: false });
      const [from, to] = rangeOf(page, pageSize);
      const { data, count, error } = await query.range(from, to);
      fail(error);
      return { rows: (data ?? []).map(mapSummary), total: count ?? 0, page, pageSize };
    },

    getById: async (id) => {
      const { data, error } = await this.sb.from('v_tender_summary').select('*').eq('id', id).maybeSingle();
      fail(error);
      return data ? mapSummary(data) : null;
    },

    getByGemId: async (gemId) => {
      const { data, error } = await this.sb.from('v_tender_summary').select('*').ilike('gem_id', gemId.trim()).maybeSingle();
      fail(error);
      return data ? mapSummary(data) : null;
    },

    isGemIdAvailable: async (gemId, excludeId) => {
      const { data, error } = await this.sb.rpc('is_gem_id_available', { p_gem_id: gemId, p_exclude_id: excludeId ?? null });
      fail(error);
      return Boolean(data);
    },

    isCrfqAvailable: async (crfq, excludeId) => {
      const { data, error } = await this.sb.rpc('is_crfq_available', { p_crfq: crfq, p_exclude_id: excludeId ?? null });
      fail(error);
      return Boolean(data);
    },

    create: async (input) => {
      const me = await this.uid();
      const { data, error } = await this.sb
        .from('tenders')
        .insert({ ...snakify({ ...input, gemId: input.gemId.trim(), tenderNumber: input.tenderNumber?.trim() || null }), created_by: me, updated_by: me })
        .select('id')
        .single();
      fail(error);
      return (await this.tenders.getById((data as Row).id as string))!;
    },

    update: async (id, patch) => {
      const me = await this.uid();
      const { error } = await this.sb.from('tenders').update({ ...snakify(patch), updated_by: me }).eq('id', id);
      fail(error);
      return (await this.tenders.getById(id))!;
    },
    remove: async (id) => {
      // Collect uploaded files first; the rows disappear with the tender (ON DELETE CASCADE).
      const { data: docs } = await this.sb.from('documents').select('storage_path').eq('tender_id', id).not('storage_path', 'is', null);
      const { error, count } = await this.sb.from('tenders').delete({ count: 'exact' }).eq('id', id);
      fail(error);
      if (!count) throw new DomainError('FORBIDDEN', 'Only administrators can delete tenders.');
      const paths = (docs ?? []).map((d: Row) => d.storage_path as string).filter(Boolean);
      if (paths.length) await this.sb.storage.from(env.documentsBucket).remove(paths); // best effort
    },
    decideBudget: async (id, input) => {
      const { error } = await this.sb.rpc('decide_tender_budget', {
        p_tender: id, p_status: input.status, p_decision_date: input.decisionDate, p_remarks: input.remarks?.trim() || null,
      });
      if (error && /decide_tender_budget/.test(error.message) && /exist|schema cache/i.test(error.message)) {
        throw new DomainError('VALIDATION', 'This needs the database update 20261103120000_department_head_gl_budget.sql.');
      }
      fail(error);
      return (await this.tenders.getById(id))!;
    },
    close: async (id, input) => {
      const me = await this.uid();
      const { data, error } = await this.sb
        .from('tenders')
        .update({
          lifecycle_status: input.outcome,
          closed_at: new Date(`${input.closedOn}T12:00:00`).toISOString(),
          closure_remarks: input.remarks.trim(),
          updated_by: me,
        })
        .eq('id', id)
        .select('id')
        .maybeSingle();
      fail(error);
      if (!data) throw new DomainError('FORBIDDEN', 'Your role does not permit closing contracts.');
      return (await this.tenders.getById(id))!;
    },
  };

  // ------------------------------------------------------------ vendors
  vendors: DataRepository['vendors'] = {
    list: async () => {
      const [{ data: vendors, error: e1 }, { data: tenders, error: e2 }] = await Promise.all([
        this.sb.from('vendors').select('*').order('name'),
        this.sb.from('v_tender_summary').select('vendor_id, status, contract_value, utilization_pct'),
      ]);
      fail(e1);
      fail(e2);
      return (vendors ?? []).map((v) => {
        const ts = (tenders ?? []).filter((t) => t.vendor_id === v.id);
        return {
          ...camelize<Vendor>(v),
          tenderCount: ts.length,
          activeCount: ts.filter((t) => t.status === 'active' || t.status === 'expiring_soon').length,
          totalContractValue: ts.reduce((s, t) => s + Number(t.contract_value), 0),
          avgUtilization: ts.length ? ts.reduce((s, t) => s + Number(t.utilization_pct), 0) / ts.length : 0,
        } satisfies VendorSummary;
      });
    },
    getById: async (id) => {
      const [{ data: v, error: e1 }, { data: tenders, error: e2 }] = await Promise.all([
        this.sb.from('vendors').select('*').eq('id', id).maybeSingle(),
        this.sb.from('v_tender_summary').select('vendor_id, status, contract_value, utilization_pct').eq('vendor_id', id),
      ]);
      fail(e1);
      fail(e2);
      if (!v) return null;
      const ts = tenders ?? [];
      return {
        ...camelize<Vendor>(v),
        tenderCount: ts.length,
        activeCount: ts.filter((t) => t.status === 'active' || t.status === 'expiring_soon').length,
        totalContractValue: ts.reduce((sum, t) => sum + Number(t.contract_value), 0),
        avgUtilization: ts.length ? ts.reduce((sum, t) => sum + Number(t.utilization_pct), 0) / ts.length : 0,
      } satisfies VendorSummary;
    },
    create: async (input) => {
      const { data, error } = await this.sb.from('vendors').insert(snakify(input)).select('*').single();
      fail(error);
      return camelize<Vendor>(data as Row);
    },
    update: async (id, patch) => {
      const { data, error } = await this.sb.from('vendors').update(snakify(patch)).eq('id', id).select('*').maybeSingle();
      fail(error);
      if (!data) throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
      return camelize<Vendor>(data as Row);
    },
    remove: async (id) => {
      const { error, count } = await this.sb.from('vendors').delete({ count: 'exact' }).eq('id', id);
      fail(error);
      if (!count) throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
    },
  };

  lookups: DataRepository['lookups'] = {
    departments: async () => {
      const { data, error } = await this.sb.from('departments').select('*').eq('is_active', true).order('name');
      fail(error);
      return (data ?? []).map((r) => camelize<Department>(r));
    },
    locations: async () => {
      const { data, error } = await this.sb.from('locations').select('*').eq('is_active', true).order('name');
      fail(error);
      return (data ?? []).map((r) => camelize<Location>(r));
    },
    approvingAuthorities: async () => {
      const { data, error } = await this.sb.from('approving_authorities').select('*').eq('is_active', true).order('sort_order').order('name');
      if (error && /approving_authorities/.test(error.message)) return []; // database update not run yet
      fail(error);
      return (data ?? []).map((r) => camelize<ApprovingAuthority>(r));
    },
    glAccounts: async () => {
      const { data, error } = await this.sb.from('gl_accounts').select('*').eq('is_active', true).order('code');
      if (error && /gl_accounts/.test(error.message)) return []; // database update not run yet
      fail(error);
      return (data ?? []).map((r) => camelize<GlAccount>(r));
    },
    budgetHeads: async () => {
      const { data, error } = await this.sb.from('budget_heads').select('*').eq('is_active', true).order('code');
      if (error && /budget_heads/.test(error.message)) return []; // database update not run yet
      fail(error);
      return (data ?? []).map((r) => camelize<BudgetHead>(r));
    },
    listAll: async (kind) => {
      const fk = LOOKUP_FK[kind];
      const select = kind === 'departments' ? '*, head:profiles!departments_head_user_id_fkey(full_name)' : '*';
      const [rows, used, items] = await Promise.all([
        kind === 'approving_authorities' ? this.sb.from(kind).select('*').order('sort_order').order('name') : this.sb.from(kind).select(select).order(kind === 'gl_accounts' || kind === 'budget_heads' ? 'code' : 'name'),
        this.sb.from('tenders').select(fk),
        kind === 'gl_accounts' ? this.sb.from('tender_line_items').select('gl_account_id') : Promise.resolve({ data: [], error: null }),
      ]);
      let rowData: unknown = rows.data;
      if (rows.error && kind === 'departments' && /head/.test(rows.error.message)) {
        // database update not run yet: list without the head
        const plain = await this.sb.from(kind).select('*').order('name');
        fail(plain.error);
        rowData = plain.data;
      } else fail(rows.error);
      fail(used.error);
      fail(items.error);
      const counts = new Map<string, number>();
      [...asRows(used.data), ...asRows(items.data)].forEach((t) => {
        const id = t[fk] as string | null;
        if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
      });
      return asRows(rowData).map((r: Row) => ({
        id: r.id as string,
        code: r.code as string,
        name: r.name as string,
        state: kind === 'locations' ? ((r.state as string | null) ?? null) : undefined,
        headUserId: kind === 'departments' ? ((r.head_user_id as string | null) ?? null) : undefined,
        headName: kind === 'departments' ? (((r.head as Row | null)?.full_name as string | undefined) ?? null) : undefined,
        businessArea: kind === 'gl_accounts' ? ((r.business_area as string | null) ?? null) : undefined,
        glGroup: kind === 'gl_accounts' ? ((r.gl_group as string | null) ?? null) : undefined,
        isActive: Boolean(r.is_active),
        usageCount: counts.get(r.id as string) ?? 0,
      }));
    },
    save: async (kind, row) => {
      const payload: Row = { code: row.code.trim().toUpperCase(), name: row.name.trim(), is_active: row.isActive };
      if (kind === 'locations') payload.state = row.state?.trim() || null;
      if (kind === 'departments' && row.headUserId !== undefined) payload.head_user_id = row.headUserId || null;
      if (kind === 'gl_accounts') {
        payload.business_area = row.businessArea?.trim() || null;
        payload.gl_group = row.glGroup?.trim() || null;
      }
      const { data, error } = row.id
        ? await this.sb.from(kind).update(payload).eq('id', row.id).select('*').maybeSingle()
        : await this.sb.from(kind).insert(payload).select('*').maybeSingle();
      fail(error);
      if (!data) throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
      return { id: data.id, code: data.code, name: data.name, state: data.state ?? null, isActive: data.is_active, usageCount: 0 };
    },
    remove: async (kind, id) => {
      const { error, count } = await this.sb.from(kind).delete({ count: 'exact' }).eq('id', id);
      fail(error);
      if (!count) throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
    },
  };

  // ------------------------------------------------------------ ledgers
  private tenderJoin = 'tender:tenders!inner(gem_id, title, unit_of_measure, vendor:vendors(name))';

  private flattenTender<T>(r: Row): T {
    const t = (r.tender ?? {}) as Row;
    const { tender: _omit, ...rest } = r;
    void _omit;
    return {
      ...camelize<Record<string, unknown>>(rest),
      gemId: t.gem_id,
      tenderTitle: t.title,
      unitOfMeasure: t.unit_of_measure,
      vendorName: (t.vendor as Row | null)?.name ?? null,
    } as T;
  }

  quantity: DataRepository['quantity'] = {
    listByTender: async (tenderId) => {
      const { data, error } = await this.sb
        .from('quantity_transactions')
        .select('*, creator:profiles(full_name)')
        .eq('tender_id', tenderId)
        .order('txn_date', { ascending: false });
      fail(error);
      return (data ?? []).map((r) => ({
        ...camelize<QuantityTransaction>(r),
        quantity: Number(r.quantity),
        itemQuantity: r.item_quantity == null ? null : Number(r.item_quantity),
        createdByName: (r.creator as Row | null)?.full_name as string | undefined,
      }));
    },
    ledger: async (q) => {
      let query = this.sb.from('quantity_transactions').select(`*, ${this.tenderJoin}`, { count: 'exact' });
      if (q.tenderId) query = query.eq('tender_id', q.tenderId);
      if (q.from) query = query.gte('txn_date', q.from);
      if (q.to) query = query.lte('txn_date', q.to);
      if (q.search?.trim()) query = query.or(`gem_id.ilike.${like(q.search)},title.ilike.${like(q.search)}`, { referencedTable: 'tender' });
      const [from, to] = rangeOf(q.page, q.pageSize);
      const { data, count, error } = await query.order('txn_date', { ascending: false }).range(from, to);
      fail(error);
      return {
        rows: asRows(data).map((r) => ({ ...this.flattenTender<QuantityLedgerRow>(r), quantity: Number(r.quantity) })),
        total: count ?? 0, page: q.page ?? 1, pageSize: q.pageSize ?? 25,
      };
    },
    create: async (input) => {
      const me = await this.uid();
      const { data, error } = await this.sb.from('quantity_transactions').insert({ ...snakify(input), created_by: me }).select('*').single();
      fail(error);
      return camelize<QuantityTransaction>(data as Row);
    },
    update: async (id, patch) => {
      const { data, error } = await this.sb
        .from('quantity_transactions')
        .update({
          txn_type: patch.txnType,
          quantity: patch.quantity,
          txn_date: patch.txnDate,
          reference_no: patch.referenceNo ?? null,
          remarks: patch.remarks ?? null,
          line_item_id: patch.lineItemId ?? null,
          item_quantity: patch.itemQuantity ?? null,
        })
        .eq('id', id)
        .select('*');
      fail(error);
      // Row-level security hides rows it refuses to change, so "nothing updated" means no permission.
      if (!data?.length) throw new DomainError('FORBIDDEN', 'This record could not be updated — it may have been deleted, or your role cannot edit it.');
      return { ...camelize<QuantityTransaction>(data[0] as Row), quantity: Number((data[0] as Row).quantity) };
    },
    remove: async (id) => {
      const { data, error } = await this.sb.from('quantity_transactions').delete().eq('id', id).select('id');
      fail(error);
      if (!data?.length) throw new DomainError('FORBIDDEN', 'This record could not be deleted — only managers and administrators can delete consumption records.');
    },
  };

  financials: DataRepository['financials'] = {
    listByTender: async (tenderId) => {
      const { data, error } = await this.sb.from('financial_transactions').select('*').eq('tender_id', tenderId).order('txn_date', { ascending: false });
      fail(error);
      return (data ?? []).map((r) => ({ ...camelize<FinancialTransaction>(r), amount: Number(r.amount), taxAmount: Number(r.tax_amount) }));
    },
    ledger: async (q) => {
      let query = this.sb.from('financial_transactions').select(`*, ${this.tenderJoin}`, { count: 'exact' });
      if (q.tenderId) query = query.eq('tender_id', q.tenderId);
      if (q.txnType) query = query.eq('txn_type', q.txnType);
      if (q.from) query = query.gte('txn_date', q.from);
      if (q.to) query = query.lte('txn_date', q.to);
      if (q.search?.trim()) query = query.or(`gem_id.ilike.${like(q.search)},title.ilike.${like(q.search)}`, { referencedTable: 'tender' });
      const [from, to] = rangeOf(q.page, q.pageSize);
      const { data, count, error } = await query.order('txn_date', { ascending: false }).range(from, to);
      fail(error);
      return {
        rows: asRows(data).map((r) => ({ ...this.flattenTender<FinancialLedgerRow>(r), amount: Number(r.amount), taxAmount: Number(r.tax_amount) })),
        total: count ?? 0, page: q.page ?? 1, pageSize: q.pageSize ?? 25,
      };
    },
    create: async (input) => {
      const me = await this.uid();
      const { data, error } = await this.sb.from('financial_transactions').insert({ ...snakify(input), created_by: me }).select('*').single();
      fail(error);
      return camelize<FinancialTransaction>(data as Row);
    },
    update: async (id, patch) => {
      const { data, error } = await this.sb.from('financial_transactions').update(snakify(patch)).eq('id', id).select('*');
      fail(error);
      if (!data?.length) throw new DomainError('FORBIDDEN', 'This entry could not be updated — it may have been deleted, or your role cannot edit it.');
      const r = data[0] as Row;
      return { ...camelize<FinancialTransaction>(r), amount: Number(r.amount), taxAmount: Number(r.tax_amount) };
    },
    remove: async (id) => {
      const { data, error } = await this.sb.from('financial_transactions').delete().eq('id', id).select('id');
      fail(error);
      if (!data?.length) throw new DomainError('FORBIDDEN', 'This entry could not be deleted — only managers and administrators can delete financial transactions.');
    },
  };

  // ------------------------------------------------------------ line items
  private mapLineItem(r: Row, usage: Row[] = []): TenderLineItem {
    const mine = usage.filter((u) => u.line_item_id === r.id);
    const used = mine.reduce((s, u) => s + (u.txn_type === 'reversal' ? -1 : 1) * Number(u.item_quantity ?? 0), 0);
    return {
      ...camelize<TenderLineItem>(r),
      quantity: Number(r.quantity), unitRate: Number(r.unit_rate), amount: Number(r.amount),
      usedQuantity: Math.round(used * 1000) / 1000, usageCount: mine.length,
    };
  }

  estimates: DataRepository['estimates'] = {
    listByTender: async (tenderId) => {
      const { data, error } = await this.sb.from('tender_monthly_estimates').select('*').eq('tender_id', tenderId).order('month');
      if (error && /tender_monthly_estimates/.test(error.message)) return []; // database update not run yet
      fail(error);
      return (data ?? []).map(mapEstimate);
    },
    listRange: async (from, to) => {
      const out: Row[] = [];
      for (let start = 0; ; start += 1000) {
        const { data, error } = await this.sb.from('tender_monthly_estimates').select('*').gte('month', from).lte('month', to).order('month').range(start, start + 999);
        if (error && /tender_monthly_estimates/.test(error.message)) return [];
        fail(error);
        out.push(...((data ?? []) as Row[]));
        if (!data || data.length < 1000) break;
      }
      return out.map(mapEstimate);
    },
    save: async (tenderId, rows) => {
      const me = await this.uid();
      const keep = rows.filter((r) => r.estimatedAmount != null);
      const drop = rows.filter((r) => r.estimatedAmount == null).map((r) => r.month);
      if (keep.length) {
        // Existing months keep their original created_by; only new months get it.
        const { data: existing, error: e0 } = await this.sb.from('tender_monthly_estimates').select('month').eq('tender_id', tenderId).in('month', keep.map((r) => r.month));
        fail(e0);
        const have = new Set(asRows(existing).map((r) => r.month as string));
        const row = (r: (typeof keep)[number]) => ({
          tender_id: tenderId, month: r.month, estimated_amount: r.estimatedAmount,
          estimated_quantity: r.estimatedQuantity ?? null, remarks: r.remarks?.trim() || null, updated_by: me,
        });
        const updates = keep.filter((r) => have.has(r.month)).map(row);
        const inserts = keep.filter((r) => !have.has(r.month)).map((r) => ({ ...row(r), created_by: me }));
        if (updates.length) {
          const { error } = await this.sb.from('tender_monthly_estimates').upsert(updates, { onConflict: 'tender_id,month' });
          fail(error);
        }
        if (inserts.length) {
          const { error } = await this.sb.from('tender_monthly_estimates').upsert(inserts, { onConflict: 'tender_id,month' });
          fail(error);
        }
      }
      if (drop.length) {
        const { error } = await this.sb.from('tender_monthly_estimates').delete().eq('tender_id', tenderId).in('month', drop);
        fail(error);
      }
      return this.estimates.listByTender(tenderId);
    },
  };

  lineItems: DataRepository['lineItems'] = {
    listByTender: async (tenderId) => {
      const [items, usage] = await Promise.all([
        this.sb.from('tender_line_items').select('*').eq('tender_id', tenderId).order('line_no'),
        this.sb.from('quantity_transactions').select('line_item_id, txn_type, item_quantity').eq('tender_id', tenderId).not('line_item_id', 'is', null),
      ]);
      if (items.error && /tender_line_items/.test(items.error.message)) return []; // database update not run yet
      fail(items.error); fail(usage.error);
      return (items.data ?? []).map((r) => this.mapLineItem(r as Row, (usage.data ?? []) as Row[]));
    },
    create: async (input) => {
      const me = await this.uid();
      const { data, error } = await this.sb.from('tender_line_items').insert({
        tender_id: input.tenderId, description: input.description.trim(), quantity: input.quantity, unit: input.unit.trim(),
        unit_rate: input.unitRate, remarks: input.remarks?.trim() || null, created_by: me,
        ...(input.glAccountId !== undefined ? { gl_account_id: input.glAccountId || null } : {}),
      }).select('*').single();
      if (error && /tender_line_items/.test(error.message) && /exist|schema cache/i.test(error.message)) {
        throw new DomainError('VALIDATION', 'Line items need the database update 20261010000001_line_items_financial_refs.sql. Run it in the Supabase SQL Editor first.');
      }
      fail(error);
      return this.mapLineItem(data as Row);
    },
    update: async (id, patch) => {
      const { data, error } = await this.sb.from('tender_line_items').update({
        description: patch.description.trim(), quantity: patch.quantity, unit: patch.unit.trim(), unit_rate: patch.unitRate, remarks: patch.remarks?.trim() || null,
        ...(patch.glAccountId !== undefined ? { gl_account_id: patch.glAccountId || null } : {}),
      }).eq('id', id).select('*');
      fail(error);
      if (!data?.length) throw new DomainError('FORBIDDEN', 'This line item could not be updated — it may have been deleted, or your role cannot edit it.');
      return this.mapLineItem(data[0] as Row);
    },
    remove: async (id) => {
      const { data, error } = await this.sb.from('tender_line_items').delete().eq('id', id).select('id');
      fail(error);
      if (!data?.length) throw new DomainError('FORBIDDEN', 'This line item could not be deleted — only managers and administrators can delete line items.');
    },
    convertTender: async (tenderId, description) => {
      const { error } = await this.sb.rpc('convert_tender_to_line_items', { p_tender: tenderId, p_description: description ?? null });
      if (error && /convert_tender_to_line_items/.test(error.message) && /function|schema cache/i.test(error.message)) {
        throw new DomainError('VALIDATION', 'Line items need the database update 20261010000001_line_items_financial_refs.sql. Run it in the Supabase SQL Editor first.');
      }
      fail(error);
    },
  };

  documents: DataRepository['documents'] = {
    listByTender: async (tenderId) => {
      const { data, error } = await this.sb
        .from('documents')
        .select('*, uploader:profiles!documents_uploaded_by_fkey(full_name)')
        .eq('tender_id', tenderId)
        .order('created_at');
      fail(error);
      return (data ?? []).map((r) => ({ ...camelize<TenderDocument>(r), uploadedByName: (r.uploader as Row | null)?.full_name as string | undefined }));
    },
    list: async (q) => {
      let query = this.sb.from('documents').select(`*, ${this.tenderJoin}`, { count: 'exact' });
      if (q.tenderId) query = query.eq('tender_id', q.tenderId);
      if (q.status === 'received') query = query.in('status', ['uploaded', 'verified']);
      else if (q.status) query = query.eq('status', q.status);
      if (q.docType) query = query.eq('doc_type', q.docType);
      if (q.search?.trim()) query = query.or(`gem_id.ilike.${like(q.search)},title.ilike.${like(q.search)}`, { referencedTable: 'tender' });
      const [from, to] = rangeOf(q.page, q.pageSize);
      const { data, count, error } = await query.order('uploaded_at', { ascending: false, nullsFirst: true }).range(from, to);
      fail(error);
      return { rows: asRows(data).map((r) => this.flattenTender<PendingDocumentRow>(r)), total: count ?? 0, page: q.page ?? 1, pageSize: q.pageSize ?? 25 };
    },
    upload: async (tenderId, file, meta) => {
      const me = await this.uid();
      const id = meta.documentId ?? crypto.randomUUID();
      const path = `${tenderId}/${id}/${Date.now()}_${file.name}`;
      const { error: upErr } = await this.sb.storage.from(env.documentsBucket).upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw new DomainError('UNKNOWN', upErr.message);
      const row = {
        id, tender_id: tenderId, doc_type: meta.docType, title: meta.title, status: 'uploaded',
        storage_path: path, file_name: file.name, mime_type: file.type, size_bytes: file.size,
        uploaded_by: me, uploaded_at: new Date().toISOString(),
      };
      const { data, error } = meta.documentId
        ? await this.sb.from('documents').update(row).eq('id', id).select('*').single()
        : await this.sb.from('documents').insert(row).select('*').single();
      fail(error);
      return camelize<TenderDocument>(data as Row);
    },
    getDownloadUrl: async (doc) => {
      if (!doc.storagePath) return null;
      const { data, error } = await this.sb.storage.from(env.documentsBucket).createSignedUrl(doc.storagePath, 300);
      if (error) throw new DomainError('UNKNOWN', error.message);
      return data.signedUrl;
    },
    getFileBlob: async (doc) => {
      if (!doc.storagePath) return null;
      const { data, error } = await this.sb.storage.from(env.documentsBucket).download(doc.storagePath);
      if (error) throw new DomainError('UNKNOWN', error.message);
      return data;
    },
    review: async (id, decision, remarks) => {
      const { data, error } = await this.sb
        .from('documents')
        .update({ status: decision, review_remarks: remarks?.trim() || null })
        .eq('id', id)
        .select('id')
        .maybeSingle();
      fail(error);
      if (!data) throw new DomainError('FORBIDDEN', 'Your role does not permit reviewing documents.');
    },
  };

  amendments: DataRepository['amendments'] = {
    listByTender: async (tenderId) => {
      const { data, error } = await this.sb.from('amendments').select('*').eq('tender_id', tenderId).order('amendment_no', { ascending: false });
      fail(error);
      return (data ?? []).map((r) => camelize<Amendment>(r));
    },
    create: async (input) => {
      const me = await this.uid();
      const tender = await this.tenders.getById(input.tenderId);
      if (!tender) throw new DomainError('NOT_FOUND', 'Tender not found.');
      const { data: last } = await this.sb.from('amendments').select('amendment_no').eq('tender_id', input.tenderId)
        .order('amendment_no', { ascending: false }).limit(1).maybeSingle();
      const row = {
        ...snakify(input),
        amendment_no: ((last?.amendment_no as number | undefined) ?? 0) + 1,
        previous_value: input.newValue != null ? tender.contractValue : null,
        previous_quantity: input.newQuantity != null ? tender.totalQuantity : null,
        previous_end_date: input.newEndDate != null ? tender.endDate : null,
        approved_by: input.status === 'approved' ? me : null,
        approved_at: input.status === 'approved' ? new Date().toISOString() : null,
        created_by: me,
      };
      const { data, error } = await this.sb.from('amendments').insert(row).select('*').single();
      fail(error);
      return camelize<Amendment>(data as Row);
    },
    listPending: async () => {
      const { data, error } = await this.sb
        .from('amendments')
        .select(`*, ${this.tenderJoin}`)
        .eq('status', 'draft')
        .order('created_at', { ascending: true });
      fail(error);
      return asRows(data).map((r) => this.flattenTender<PendingAmendmentRow>(r));
    },
    review: async (id, decision, remarks) => {
      const { data, error } = await this.sb
        .from('amendments')
        .update({ status: decision, review_remarks: remarks?.trim() || null })
        .eq('id', id)
        .select('id')
        .maybeSingle();
      fail(error);
      if (!data) throw new DomainError('FORBIDDEN', 'Your role does not permit approving amendments.');
    },
  };

  audit: DataRepository['audit'] = {
    list: async (q) => {
      let query = this.sb
        .from('audit_logs')
        .select('*, actor:profiles(full_name), tender:tenders(gem_id)', { count: 'exact' });
      if (q.tenderId) query = query.eq('tender_id', q.tenderId);
      if (q.entityType) query = query.eq('entity_type', q.entityType);
      if (q.action) query = query.eq('action', q.action);
      if (q.from) query = query.gte('occurred_at', q.from);
      if (q.to) query = query.lte('occurred_at', `${q.to}T23:59:59`);
      if (q.search?.trim()) query = query.or(`actor_email.ilike.${like(q.search)},summary.ilike.${like(q.search)}`);
      const [from, to] = rangeOf(q.page, q.pageSize);
      const { data, count, error } = await query.order('occurred_at', { ascending: false }).range(from, to);
      fail(error);
      return {
        rows: (data ?? []).map((r) => ({
          ...camelize<AuditLog>(r),
          actorName: (r.actor as Row | null)?.full_name as string | undefined,
          gemId: (r.tender as Row | null)?.gem_id as string | undefined,
        })),
        total: count ?? 0, page: q.page ?? 1, pageSize: q.pageSize ?? 25,
      };
    },
  };

  notifications: DataRepository['notifications'] = {
    listMine: async () => {
      const { data, error } = await this.sb.from('notifications').select('*').order('created_at', { ascending: false }).limit(50);
      fail(error);
      return (data ?? []).map((r) => camelize<AppNotification>(r));
    },
    markRead: async (id) => {
      const { error } = await this.sb.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id);
      fail(error);
    },
    markAllRead: async () => {
      const { error } = await this.sb.from('notifications').update({ read_at: new Date().toISOString() }).is('read_at', null);
      fail(error);
    },
    runChecks: async () => {
      const { data, error } = await this.sb.rpc('run_daily_alerts');
      if (error && /run_daily_alerts/.test(error.message)) {
        // Email migration not installed yet — fall back to in-app alerts only.
        const fallback = await this.sb.rpc('generate_expiry_notifications');
        fail(fallback.error);
        return { created: Number(fallback.data ?? 0), emailed: 0, emailError: null };
      }
      fail(error);
      const r = (data ?? {}) as { created?: number; owner_alerts?: number; emailed?: number; email_error?: string | null };
      return { created: Number(r.created ?? 0), ownerAlerts: Number(r.owner_alerts ?? 0), emailed: Number(r.emailed ?? 0), emailError: r.email_error ?? null };
    },
  };

  email: DataRepository['email'] = {
    keyConfigured: async () => {
      const { data, error } = await this.sb.rpc('email_key_configured');
      if (error) return false;
      return Boolean(data);
    },
    sendTest: async () => {
      const { data, error } = await this.sb.rpc('send_test_email');
      if (error) throw new DomainError(error.code === '42501' ? 'FORBIDDEN' : 'VALIDATION', error.message);
      // Older database function returned just the address.
      if (typeof data === 'string') return { requestId: null, to: data };
      const r = (data ?? {}) as { request_id?: number; to?: string };
      return { requestId: r.request_id != null ? Number(r.request_id) : null, to: String(r.to ?? '') };
    },
    result: async (requestId) => {
      const { data, error } = await this.sb.rpc('email_request_result', { p_request_id: requestId });
      if (error) throw new DomainError('UNKNOWN', error.message);
      const r = (data ?? {}) as { pending?: boolean; status_code?: number; content?: string | null; error?: string | null; ok?: boolean; detail?: string | null };
      return { pending: !!r.pending, statusCode: r.status_code, content: r.content ?? null, error: r.error ?? null, ok: r.ok, detail: r.detail ?? null };
    },
  };

  users: DataRepository['users'] = {
    list: async () => {
      const { data, error } = await this.sb.from('profiles').select('*, department:departments!profiles_department_id_fkey(name)').order('full_name');
      fail(error);
      return (data ?? []).map((r) => ({ ...camelize<AppUser>(r), departmentName: (r.department as Row | null)?.name as string | undefined }));
    },
    updateRole: async (id, role) => {
      const { error } = await this.sb.from('profiles').update({ role }).eq('id', id);
      fail(error);
    },
    setActive: async (id, active) => {
      const { error } = await this.sb.from('profiles').update({ is_active: active }).eq('id', id);
      fail(error);
    },
    updateContact: async (id, patch) => {
      const row: Row = {};
      if (patch.fullName !== undefined) row.full_name = patch.fullName;
      if (patch.designation !== undefined) row.designation = patch.designation;
      if (patch.phone !== undefined) row.phone = patch.phone;
      if (patch.departmentId !== undefined) row.department_id = patch.departmentId || null;
      const { data, error } = await this.sb.from('profiles').update(row).eq('id', id).select('id').maybeSingle();
      if (error?.code === 'PGRST204' || /column .*phone/i.test(error?.message ?? '')) {
        throw new DomainError('VALIDATION', 'Phone numbers need the 6 Oct database update (20261006000001_contacts_owner_alerts.sql). Run it in the Supabase SQL Editor first.');
      }
      fail(error);
      if (!data) throw new DomainError('FORBIDDEN', 'Only administrators can change other users’ details.');
    },
    updateEmail: async (id, email) => {
      const { data, error } = await this.sb.rpc('admin_update_user_email', { p_user_id: id, p_email: email.trim() });
      if (error && /admin_update_user_email/.test(error.message) && /exist|schema cache/i.test(error.message)) {
        throw new DomainError('VALIDATION', 'Changing an email needs the database update 20261104120000_gl_details_user_admin.sql.');
      }
      if (error?.code === '23505') throw new DomainError('VALIDATION', 'Another user already has this email.');
      fail(error);
      return String(data ?? email.trim().toLowerCase());
    },
    create: async (u) => {
      const { data, error } = await this.sb.rpc('admin_create_user', {
        p_email: u.email.trim(),
        p_password: u.password,
        p_full_name: u.fullName.trim(),
        p_role: u.role,
        p_phone: u.phone || null,
        p_designation: u.designation || null,
        p_department_id: u.departmentId || null,
        p_must_change_password: u.mustChangePassword,
      });
      if (error) {
        if (/admin_create_user/.test(error.message) && /function|schema cache/i.test(error.message)) {
          throw new DomainError('VALIDATION', 'Adding users needs the database update 20261006000002_admin_user_management.sql. Run it in the Supabase SQL Editor first.');
        }
        throw new DomainError(error.code === '42501' ? 'FORBIDDEN' : 'VALIDATION', error.message);
      }
      return String(data);
    },
    resetPassword: async (id, password, mustChangePassword) => {
      const { error } = await this.sb.rpc('admin_set_user_password', { p_user_id: id, p_password: password, p_must_change_password: mustChangePassword });
      if (error) throw new DomainError(error.code === '42501' ? 'FORBIDDEN' : 'VALIDATION', error.message);
    },
    ownedTenderCount: async (id) => {
      const { count, error } = await this.sb.from('tenders').select('id', { count: 'exact', head: true }).eq('owner_id', id);
      fail(error);
      return count ?? 0;
    },
    remove: async (id, transferTo) => {
      const { data, error } = await this.sb.rpc('admin_delete_user', { p_user_id: id, p_transfer_to: transferTo ?? null });
      if (error) {
        if (/admin_delete_user/.test(error.message) && /function|schema cache/i.test(error.message)) {
          throw new DomainError('VALIDATION', 'Deleting users needs the database update 20261007000001_delete_users.sql. Run it in the Supabase SQL Editor first.');
        }
        throw new DomainError(error.code === '42501' ? 'FORBIDDEN' : 'VALIDATION', error.message);
      }
      return { tendersTransferred: Number((data as { tenders_transferred?: number } | null)?.tenders_transferred ?? 0) };
    },
  };

  // ------------------------------------------------------------ owner alerts
  alerts: DataRepository['alerts'] = {
    list: async (q) => {
      // Pick up provider results (sent / failed) before showing statuses. Ignore if not installed.
      await this.sb.rpc('refresh_alert_delivery_status');
      let eventIds: string[] | null = null;
      if (q.status || q.channel) {
        let dq = this.sb.from('alert_deliveries').select('event_id').limit(1000);
        if (q.status) dq = dq.eq('status', q.status);
        if (q.channel) dq = dq.eq('channel', q.channel);
        if (q.tenderId) dq = dq.eq('tender_id', q.tenderId);
        const { data, error } = await dq;
        fail(error);
        eventIds = [...new Set<string>((data ?? []).map((r: Row) => r.event_id as string))];
        if (!eventIds.length) return { rows: [], total: 0, page: q.page ?? 1, pageSize: q.pageSize ?? 25 };
      }
      const term = q.search?.trim();
      let query = this.sb
        .from('tender_alert_events')
        .select(`*, tender:tenders${term ? '!inner' : ''}(gem_id, title), owner:profiles(full_name), deliveries:alert_deliveries(*)`, { count: 'exact' });
      if (q.tenderId) query = query.eq('tender_id', q.tenderId);
      if (eventIds) query = query.in('id', eventIds);
      if (term) query = query.ilike('tender.gem_id', like(term));
      const [from, to] = rangeOf(q.page, q.pageSize);
      const { data, count, error } = await query.order('created_at', { ascending: false }).range(from, to);
      fail(error);
      const order: Record<string, number> = { in_app: 0, email: 1, sms: 2, whatsapp: 3 };
      return {
        rows: (data ?? []).map((r: Row) => {
          const { tender, owner, deliveries, ...rest } = r;
          return {
            ...camelize<TenderAlertEvent>(rest),
            gemId: ((tender as Row | null)?.gem_id as string) ?? '—',
            tenderTitle: ((tender as Row | null)?.title as string) ?? '',
            ownerName: ((owner as Row | null)?.full_name as string | undefined) ?? null,
            deliveries: ((deliveries as Row[] | null) ?? [])
              .map((d) => camelize<AlertDelivery>(d))
              .sort((a, b) => order[a.channel] - order[b.channel]),
          } satisfies TenderAlertEvent;
        }),
        total: count ?? 0, page: q.page ?? 1, pageSize: q.pageSize ?? 25,
      };
    },
    retry: async (deliveryId) => {
      const { data, error } = await this.sb.rpc('retry_alert_delivery', { p_id: deliveryId });
      if (error) throw new DomainError(error.code === '42501' ? 'FORBIDDEN' : 'VALIDATION', error.message);
      return String(data) as AlertDeliveryStatus;
    },
    textChannels: async () => {
      const { data, error } = await this.sb.rpc('text_channels_status');
      if (error || !data) return { sms: { provider: 'none', ready: false }, whatsapp: { provider: 'none', ready: false } };
      return data as TextChannelStatus;
    },
  };

  analytics: DataRepository['analytics'] = {
    consumptionDataset: async () => {
      const fetchAll = async <T,>(table: string, columns: string, order: string) => {
        const out: T[] = [];
        for (let from = 0; ; from += 1000) {
          const { data, error } = await this.sb.from(table).select(columns).order(order).range(from, from + 999);
          fail(error);
          out.push(...((data ?? []) as T[]));
          if (!data || data.length < 1000) break;
        }
        return out;
      };
      const [tenders, txns] = await Promise.all([
        fetchAll<Row>('v_tender_summary', '*', 'id'),
        fetchAll<Row>('quantity_transactions', 'tender_id, txn_date, txn_type, quantity', 'txn_date'),
      ]);
      return {
        tenders: tenders.map(mapSummary),
        transactions: txns.map((t) => ({
          tenderId: t.tender_id as string,
          txnDate: t.txn_date as string,
          txnType: t.txn_type as QuantityTransaction['txnType'],
          quantity: Number(t.quantity),
        })),
      };
    },
  };

  settings: DataRepository['settings'] = {
    get: async () => {
      const { data, error } = await this.sb.from('app_settings').select('key, value');
      fail(error);
      const map = Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
      return {
        organizationName: (map.organization_name as string) ?? 'Your Organization',
        expiringThresholdDays: Number(map.expiring_threshold_days ?? 60),
        billLagDays: Number(map.bill_lag_days ?? 60),
        defaultCurrency: (map.default_currency as string) ?? 'INR',
        lastBackupAt: (map.last_backup_at as string | undefined) ?? null,
        emailEnabled: map.email_enabled === true || map.email_enabled === 'true',
        emailProvider: map.email_provider === 'gmail_relay' ? 'gmail_relay' : 'brevo',
        emailRelayUrl: (map.email_relay_url as string | undefined) ?? '',
        emailFrom: (map.email_from as string | undefined) ?? '',
        emailFromName: (map.email_from_name as string | undefined) ?? 'TCMS Alerts',
        appUrl: (map.app_url as string | undefined) ?? window.location.origin,
      } satisfies AppSettings;
    },
    update: async (patch) => {
      const rows = Object.entries(patch).map(([k, v]) => ({ key: toSnake(k), value: v }));
      const { error } = await this.sb.from('app_settings').upsert(rows);
      fail(error);
      return this.settings.get();
    },
  };

  digest: DataRepository['digest'] = {
    preview: async (departmentId) => {
      const { data, error } = await this.sb.rpc('weekly_digest_html', { p_department: departmentId });
      if (error && /weekly_digest_html/.test(error.message) && /exist|schema cache/i.test(error.message)) {
        throw new DomainError('VALIDATION', 'The weekly email needs the database update 20261105130000_weekly_digest.sql.');
      }
      fail(error);
      return String(data ?? '');
    },
    sendNow: async () => {
      const { data, error } = await this.sb.rpc('send_weekly_digest');
      fail(error);
      const r = (data ?? {}) as Row;
      return { sent: Number(r.sent ?? 0), skipped: Number(r.skipped ?? 0), failed: Number(r.failed ?? 0), emailError: (r.email_error as string | null) ?? null };
    },
  };

  dashboard: DataRepository['dashboard'] = {
    summary: async () => {
      const [all, pending, activity] = await Promise.all([
        this.sb.from('v_tender_summary').select('*'),
        this.documents.list({ status: 'pending', page: 1, pageSize: 8 }),
        this.audit.list({ page: 1, pageSize: 10 }),
      ]);
      fail(all.error);
      const sortedPending = [...pending.rows].sort((a, b) => ((a.dueDate ?? '') > (b.dueDate ?? '') ? 1 : -1));
      return computeDashboard((all.data ?? []).map(mapSummary), sortedPending, activity.rows);
    },
  };

  backup: DataRepository['backup'] = {
    exportAll: async () => {
      // PostgREST returns at most 1000 rows per request, so page through each table.
      const fetchAll = async (table: string, order: string) => {
        const out: Row[] = [];
        for (let from = 0; ; from += 1000) {
          const { data, error } = await this.sb.from(table).select('*').order(order).range(from, from + 999);
          fail(error);
          out.push(...((data ?? []) as Row[]));
          if (!data || data.length < 1000) break;
        }
        return out;
      };
      const plan: [string, string, string][] = [
        ['Tender summary', 'v_tender_summary', 'gem_id'],
        ['tenders', 'tenders', 'gem_id'],
        ['vendors', 'vendors', 'vendor_code'],
        ['departments', 'departments', 'code'],
        ['locations', 'locations', 'code'],
        ['quantity_transactions', 'quantity_transactions', 'txn_date'],
        ['financial_transactions', 'financial_transactions', 'txn_date'],
        ['documents', 'documents', 'created_at'],
        ['amendments', 'amendments', 'created_at'],
        ['profiles', 'profiles', 'created_at'],
        ['audit_logs', 'audit_logs', 'occurred_at'],
        ['app_settings', 'app_settings', 'key'],
      ];
      const result: { name: string; rows: Record<string, unknown>[] }[] = [];
      for (const [name, table, order] of plan) result.push({ name, rows: await fetchAll(table, order) });
      // Alert history (only present after the 6 Oct database update).
      for (const table of ['tender_line_items', 'tender_monthly_estimates', 'tender_alert_events', 'alert_deliveries']) {
        try {
          result.push({ name: table, rows: await fetchAll(table, 'created_at') });
        } catch {
          /* table not installed yet */
        }
      }
      return result;
    },
  };

  search: DataRepository['search'] = {
    global: async (term) => {
      const v = like(term);
      const [t, vd] = await Promise.all([
        this.sb.from('v_tender_summary').select('id, gem_id, tender_number, title, vendor_name, status')
          .or(`gem_id.ilike.${v},tender_number.ilike.${v},po_number.ilike.${v},contract_number.ilike.${v},title.ilike.${v},vendor_name.ilike.${v}`)
          .limit(6),
        this.sb.from('vendors').select('id, name, vendor_code').or(`name.ilike.${v},vendor_code.ilike.${v}`).limit(4),
      ]);
      fail(t.error);
      fail(vd.error);
      return {
        tenders: (t.data ?? []).map((r) => camelize<Pick<TenderSummary, 'id' | 'gemId' | 'tenderNumber' | 'title' | 'vendorName' | 'status'>>(r)),
        vendors: (vd.data ?? []).map((r) => camelize<Pick<Vendor, 'id' | 'name' | 'vendorCode'>>(r)),
      };
    },
  };
}
